import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { registroLimpiezaService } from '../services/registroLimpiezaService';
import { CrearRegistroLimpieza, ActualizarRegistroLimpieza } from '../types';

export const useRegistrosLimpieza = (
  page: number = 1, 
  pageSize: number = 50,
  searchTerm?: string,
  usuarioId?: number,
  estado?: string,
  fechaInicio?: string,
  fechaFin?: string
) => {
  const queryClient = useQueryClient();

  // Determinar si hay filtros activos
  const hasFilters = searchTerm || 
    (usuarioId && usuarioId !== 0) || 
    (estado && estado !== 'todos') || 
    fechaInicio || 
    fechaFin;

  const {
    data: registrosLimpiezaPaginated,
    isLoading,
    error,
    refetch: refetchRegistros,
  } = useQuery({
    queryKey: ['registros-limpieza', page, pageSize, searchTerm, usuarioId, estado, fechaInicio, fechaFin],
    queryFn: async () => {
      const response = hasFilters 
        ? await registroLimpiezaService.getRegistrosFilteredPaginated(page, pageSize, searchTerm, usuarioId, estado, fechaInicio, fechaFin)
        : await registroLimpiezaService.getRegistrosLimpiezaPaginated(page, pageSize);
      
      if (!response.success) {
        throw new Error(response.message || 'Error al cargar los registros de limpieza');
      }
      return response.data;
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Extraer los registros del resultado paginado
  const registrosLimpieza = registrosLimpiezaPaginated?.data || [];

  const createRegistroMutation = useMutation({
    mutationFn: async (registro: CrearRegistroLimpieza) => {
      const response = await registroLimpiezaService.createRegistroLimpieza(registro);
      if (!response.success) {
        throw new Error(response.message || 'Error al crear el registro de limpieza');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registros-limpieza'] });
    },
    onError: () => {},
  });

  const updateRegistroMutation = useMutation({
    mutationFn: async ({ id, registro }: { id: number; registro: ActualizarRegistroLimpieza }) => {
      const response = await registroLimpiezaService.updateRegistroLimpieza(id, registro);
      if (!response.success) {
        throw new Error(response.message || 'Error al actualizar el registro de limpieza');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registros-limpieza'] });
    },
    onError: () => {},
  });

  const deleteRegistroMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await registroLimpiezaService.deleteRegistroLimpieza(id);
      if (!response.success) {
        throw new Error(response.message || 'Error al eliminar el registro de limpieza');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['registros-limpieza'] });
    },
    onError: () => {},
  });

  return {
    registrosLimpieza: registrosLimpieza || [],
    registrosLimpiezaPaginated: registrosLimpiezaPaginated,
    isLoading,
    error,
    createRegistro: createRegistroMutation.mutate,
    updateRegistro: updateRegistroMutation.mutate,
    deleteRegistro: deleteRegistroMutation.mutate,
    isCreating: createRegistroMutation.isPending,
    isUpdating: updateRegistroMutation.isPending,
    isDeleting: deleteRegistroMutation.isPending,
    refetchRegistros,
    createError: createRegistroMutation.error,
    updateError: updateRegistroMutation.error,
    deleteError: deleteRegistroMutation.error,
  };
};

export const useRutasByRangoFechas = (fechaInicio: string, fechaFin: string, usuarioId?: number) => {
  return useQuery({
    queryKey: ['rutas-rango-fechas', fechaInicio, fechaFin, usuarioId],
    queryFn: () => registroLimpiezaService.getRutasByRangoFechas(fechaInicio, fechaFin, usuarioId),
    enabled: !!fechaInicio && !!fechaFin
  });
};

export const useUsuariosActivos = () => {
  return useQuery({
    queryKey: ['usuarios-activos'],
    queryFn: () => registroLimpiezaService.getUsuariosActivos()
  });
}; 